/**
 * Traces - spatial evidence granny can discover and tidy up.
 *
 * This is the system that gives mischief a delayed cost. Breaking a vase while
 * she is in another room is nearly free at the moment it happens, but it leaves
 * shards. Those shards pin Heat decay to zero whenever she is in that room, and
 * spike Heat the moment she actually spots them.
 *
 * Pure data + pure functions. No Three.js, no Rapier - see heat.ts for why.
 */

import type { TraceKind } from '@/core/events';
import type { Vec3 } from '@/core/math';
import { clone, distanceXZ } from '@/core/math';
import { tuning } from '@/core/tuning';

export interface Trace {
  id: string;
  kind: TraceKind;
  position: Vec3;
  /** Which room this trace lives in. Drives the "unresolved in granny's room" check. */
  roomId: string;
  /** Heat added when granny first spots it. */
  heatOnFound: number;
  /** Granny has seen it but has not finished tidying it. */
  found: boolean;
  /** Fully cleaned up - no longer blocks heat decay. */
  resolved: boolean;
  /** Seconds spent tidying so far. */
  resolveProgress: number;
  /** Some traces (a running faucet) get worse the longer they are ignored. */
  growth?: {
    /** Extra heatOnFound added per second, up to `max`. */
    perSecond: number;
    max: number;
    elapsed: number;
  };
}

export interface TraceStore {
  traces: Trace[];
  nextId: number;
}

export function createTraceStore(): TraceStore {
  return { traces: [], nextId: 1 };
}

export function addTrace(
  store: TraceStore,
  params: {
    kind: TraceKind;
    position: Vec3;
    roomId: string;
    heatOnFound: number;
    growth?: { perSecond: number; max: number };
  },
): Trace {
  const trace: Trace = {
    id: `trace_${store.nextId++}`,
    kind: params.kind,
    position: clone(params.position),
    roomId: params.roomId,
    heatOnFound: params.heatOnFound,
    found: false,
    resolved: false,
    resolveProgress: 0,
    ...(params.growth ? { growth: { ...params.growth, elapsed: 0 } } : {}),
  };
  store.traces.push(trace);
  return trace;
}

/** Unresolved traces, i.e. the ones that still matter to the heat system. */
export function unresolvedTraces(store: TraceStore): Trace[] {
  return store.traces.filter((t) => !t.resolved);
}

/** True when any unresolved trace sits in `roomId`. This is what pins heat decay. */
export function hasUnresolvedTraceInRoom(store: TraceStore, roomId: string): boolean {
  return store.traces.some((t) => !t.resolved && t.roomId === roomId);
}

/**
 * The unresolved trace granny should walk to next: nearest first, but
 * already-found ones take priority since she is mid-cleanup on those.
 */
export function nearestUnresolvedTrace(store: TraceStore, from: Vec3): Trace | undefined {
  let best: Trace | undefined;
  let bestScore = Infinity;
  for (const trace of store.traces) {
    if (trace.resolved) continue;
    // Subtracting a constant for found traces biases her toward finishing a job.
    const score = distanceXZ(from, trace.position) - (trace.found ? 100 : 0);
    if (score < bestScore) {
      bestScore = score;
      best = trace;
    }
  }
  return best;
}

/**
 * Granny discovers a trace. Returns the heat to add, or 0 if it was already
 * found. Growth is baked in at discovery time, so a faucet left running for a
 * minute is genuinely worse than one caught immediately.
 */
export function findTrace(trace: Trace): number {
  if (trace.found) return 0;
  trace.found = true;
  const bonus = trace.growth
    ? Math.min(trace.growth.max, trace.growth.perSecond * trace.growth.elapsed)
    : 0;
  return trace.heatOnFound + bonus;
}

/** Advances cleanup on a found trace. Returns true on the step it resolves. */
export function tidyTrace(trace: Trace, dt: number): boolean {
  if (trace.resolved || !trace.found) return false;
  trace.resolveProgress += dt;
  if (trace.resolveProgress < tuning.traces.resolveSeconds) return false;
  trace.resolved = true;
  return true;
}

/** Per-step growth for traces that get worse when ignored. */
export function stepTraces(store: TraceStore, dt: number): void {
  for (const trace of store.traces) {
    if (trace.resolved || !trace.growth) continue;
    trace.growth.elapsed += dt;
  }
}

/**
 * Traces within granny's discovery radius that she could plausibly notice.
 * The caller supplies `hasLineOfSight` because LOS needs the physics world,
 * which this module deliberately does not import.
 */
export function tracesInDiscoveryRange(
  store: TraceStore,
  eye: Vec3,
  hasLineOfSight: (to: Vec3) => boolean,
): Trace[] {
  const radius = tuning.traces.discoveryRadius;
  return store.traces.filter((trace) => {
    if (trace.resolved || trace.found) return false;
    if (distanceXZ(eye, trace.position) > radius) return false;
    if (tuning.traces.discoveryRequiresLos && !hasLineOfSight(trace.position)) return false;
    return true;
  });
}

/** Level teardown. */
export function clearTraces(store: TraceStore): void {
  store.traces.length = 0;
  store.nextId = 1;
}
